import { format, parseISO } from 'date-fns'
import type { ReviewStats } from '@/api/client'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

type Window = ReviewStats['year_over_year']['this_year']

function delta(now: number | null, then: number | null, unit: '' | '%' | 'pt', digits = 0) {
  if (now == null || then == null) return null
  const d = now - then
  const sign = d > 0 ? '+' : ''
  const txt = `${sign}${d.toFixed(digits)}${unit}`
  return { d, txt }
}

function Kpi({
  label,
  value,
  compare,
  betterWhen,
  tip,
}: {
  label: string
  value: string
  compare: { d: number; txt: string; against: string } | null
  betterWhen: 'up' | 'down'
  tip: string
}) {
  const cls =
    compare == null || compare.d === 0
      ? 'text-textMuted'
      : compare.d > 0 === (betterWhen === 'up')
        ? 'text-mint'
        : 'text-danger'
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          tabIndex={0}
          className="cursor-default rounded-card border border-border bg-surface px-4 py-3 shadow-md focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint"
        >
          <div className="text-[11px] uppercase tracking-wide text-textMuted">{label}</div>
          <div className="text-2xl font-semibold tabular-nums text-text">{value}</div>
          <div className={`text-xs tabular-nums ${cls}`}>
            {compare
              ? `${compare.txt} vs last year to date (${compare.against})`
              : 'no comparison yet'}
          </div>
        </div>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{tip}</TooltipContent>
    </Tooltip>
  )
}

/**
 * The portfolio at a glance, every number this year to date against the
 * same span of last year. No rolling windows: the season is what the
 * operator compares against, and last year's is the baseline.
 */
export function KpiStrip({ stats, openAttention }: { stats: ReviewStats; openAttention: number }) {
  const ty: Window = stats.year_over_year.this_year
  const ly: Window = stats.year_over_year.last_year
  const fiveShare = (w: Window) => (w.reviews ? Math.round((100 * w.five_star) / w.reviews) : null)
  const asOf = format(parseISO(stats.year_over_year.as_of), 'MMM d')

  const avg = delta(ty.avg, ly.avg, '', 2)
  const share = delta(fiveShare(ty), fiveShare(ly), 'pt')
  const rate = delta(ty.review_rate, ly.review_rate, 'pt')
  const count = delta(ty.reviews, ly.reviews, '')
  const sub = delta(ty.sub_five, ly.sub_five, '')

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
      <Kpi
        label={`Average rating, ${stats.year_over_year.as_of.slice(0, 4)}`}
        value={ty.avg == null ? '-' : ty.avg.toFixed(2)}
        compare={avg && { ...avg, against: ly.avg == null ? '-' : ly.avg.toFixed(2) }}
        betterWhen="up"
        tip={`Mean of ${ty.reviews} reviews received from Jan 1 to ${asOf} this year, against ${ly.reviews} in the same span last year. All time: ${stats.portfolio.avg == null ? '-' : stats.portfolio.avg.toFixed(2)} over ${stats.portfolio.reviews} reviews.`}
      />
      <Kpi
        label="Five-star share"
        value={fiveShare(ty) == null ? '-' : `${fiveShare(ty)}%`}
        compare={share && { ...share, against: fiveShare(ly) == null ? '-' : `${fiveShare(ly)}%` }}
        betterWhen="up"
        tip={`${ty.five_star} of ${ty.reviews} reviews this year to date were five stars.`}
      />
      <Kpi
        label="Review rate"
        value={ty.review_rate == null ? '-' : `${ty.review_rate}%`}
        compare={rate && { ...rate, against: ly.review_rate == null ? '-' : `${ly.review_rate}%` }}
        betterWhen="up"
        tip={`${ty.reviewed} of ${ty.stays} stays that checked out this year (and whose review window has closed) left a review. Stays still inside the window are not counted.`}
      />
      <Kpi
        label="Reviews received"
        value={String(ty.reviews)}
        compare={count && { ...count, against: String(ly.reviews) }}
        betterWhen="up"
        tip="Pace: reviews landed this year to date vs the same date last year."
      />
      <Kpi
        label="Below five"
        value={String(ty.sub_five)}
        compare={sub && { ...sub, against: String(ly.sub_five) }}
        betterWhen="down"
        tip={`${ty.sub_five} reviews under five stars this year to date. ${openAttention} attention item${openAttention === 1 ? '' : 's'} open.`}
      />
    </div>
  )
}
